/* eslint-disable max-lines */
import React, {
  useEffect,
  useState,
  FunctionComponent,
  useCallback,
  KeyboardEvent,
} from 'react';
import { Box, Card, Search } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import {
  askingAQuestion,
  clickOnAnswer,
  gotNoAnswer,
  clickTheSeeAllResultsLink,
} from '@wix/bi-logger-new-help-center/v2';
import {
  useFedops,
  useHttpClient,
  useExperiments,
} from '@wix/fe-essentials-standalone';
import { v4 as uuidv4 } from 'uuid';
import { DATA_HOOKS } from '../../dataHooks';
import { BI, EXPERIMENTS, FEDOPS, LOCALES, PAGES } from '../../constants';
import {
  ArticleSearchResult,
  ArticleSearchResultItem,
  ExternalSearchResult,
  PageType,
  ExternalResourceType,
  NormalizedResource,
} from '../../types';
import { DeprecatedSearchBarResults } from './DeprecatedSearchBarResults';
import { DeprecatedEmptySearchResults } from './DeprecatedEmptySearchResults';
import { useBI } from '../../hooks/useBI';
import {
  mapArticleTypeToItemType,
  mapPageTypeToSourceName,
} from '../../utils/bi';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import css from './index.module.scss';
import { pushUrl } from '../InnerLink';
import { SearchBarResults } from './SearchBarResults';
import { EmptySearchResults } from './EmptySearchResults';
import classNames from 'classnames';

type SearchBarProps = {
  pageType: PageType;
  itemId?: string;
  itemType?: string;
  homepageNewDesign?: boolean;
};

const allExternalResources = [
  ExternalResourceType.WIX_BLOG,
  ExternalResourceType.WIX_LEARN,
  ExternalResourceType.WIX_SEO_LEARN,
  ExternalResourceType.WIX_VELO,
];

export const SearchBar: FunctionComponent<SearchBarProps> = ({
  pageType,
  itemId,
  itemType,
  homepageNewDesign,
}: SearchBarProps) => {
  const { t } = useTranslation();
  const { locale, asPath } = useRouter();
  const httpClient = useHttpClient();
  const { experiments } = useExperiments({ readOnly: true });
  const [hideResults, setHideResults] = useState(false);
  const { sendBIEvent } = useBI();
  const fedopsApi = useFedops();
  const ref = useOutsideClick(() => {
    setHideResults(true);
  });

  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchSessionId, setSearchSessionId] = useState(uuidv4());
  const [articles, setArticles] = useState<ArticleSearchResultItem[] | null>(
    null
  );
  const [focusedArticleIndex, setFocusedArticleIndex] = useState<number>(-1);
  const [externalSearchResults, setExternalSearchResults] =
    useState<NormalizedResource[]>();

  const showNewSearchBarResults =
    locale === LOCALES.EN ||
    experiments.enabled(
      EXPERIMENTS.SPECS.NEW_SEARCH_RESULTS_PAGE_ALL_LOCALES
    ) ||
    experiments.enabled(
      EXPERIMENTS.SPECS.NEW_SEARCH_BAR_RESULTS_MODAL_ALL_LOCALES
    );

  const isExternalSearchResultsEnabled = experiments.enabled(
    EXPERIMENTS.SPECS.EXTERNAL_SEARCH
  );

  const sendNoResultsBiEvent = useCallback(
    async (data: ArticleSearchResult) => {
      await sendBIEvent(
        gotNoAnswer({
          kb_lang: locale as string,
          source_name: mapPageTypeToSourceName(pageType),
          search_method: data.searchMethod,
          search_version: data.searchVersion,
          question: inputText,
        })
      );
    },
    [locale, pageType, itemType, inputText]
  );

  const search = useCallback(async (): Promise<void> => {
    if (inputText?.length > 0) {
      setIsLoading(true);
      fedopsApi.interactionStarted(FEDOPS.INTERACTION_NAMES.SEARCH_ARTICLES);
      const { data } = await httpClient.get<ArticleSearchResult>(
        '/api/article/search',
        {
          params: {
            locale,
            text: inputText,
          },
        }
      );
      fedopsApi.interactionEnded(FEDOPS.INTERACTION_NAMES.SEARCH_ARTICLES);

      const newArticles = data.items.slice(0, showNewSearchBarResults ? 6 : 5);
      setFocusedArticleIndex(-1);
      setArticles(newArticles);
      setIsLoading(false);
      await sendBIEvent(
        askingAQuestion({
          source_name: mapPageTypeToSourceName(pageType),
          item_id: itemId,
          item_type: itemType,
          kb_lang: locale as string,
          presented_content: JSON.stringify(
            newArticles.map((article, index) => ({
              order: index + 1,
              item_id: article.id,
              locale,
              type: mapArticleTypeToItemType(article.type),
            }))
          ),
          question: inputText,
          search_session_Id: searchSessionId,
          search_method: data.searchMethod,
          search_version: data.searchVersion,
        })
      );
      if (newArticles.length === 0) {
        await sendNoResultsBiEvent(data);
      }
    } else {
      setArticles(null);
    }
  }, [inputText]);

  const dataToExternalSearchResults = (
    data: Record<string, ExternalSearchResult>
  ) => {
    const searchResultsToAdd = [];
    for (const resourceKey in data) {
      if (data[resourceKey].items && data[resourceKey].items.length > 0) {
        searchResultsToAdd.push(...data[resourceKey].items);
      }
    }
    return searchResultsToAdd;
  };
  const externalSearch = useCallback(async (): Promise<void> => {
    if (inputText?.length > 0) {
      setIsLoading(true);
      const { data } = await httpClient.get<
        Record<string, ExternalSearchResult>
      >('/api/externalSearch/search', {
        params: {
          query: inputText,
          page: 1,
          externalResourceType: allExternalResources,
          locale,
        },
      });
      const results = dataToExternalSearchResults(data);
      setExternalSearchResults(results);
      setFocusedArticleIndex(-1);
      setIsLoading(false);
    } else {
      setExternalSearchResults([]);
    }
  }, [inputText]);

  const onClear = () => {
    setInputText('');
    setArticles(null);
    setFocusedArticleIndex(-1);
    setSearchSessionId(uuidv4());
  };

  const onSearchResultClick = async (
    { url, type, id }: ArticleSearchResultItem,
    index: number
  ): Promise<void> => {
    await sendBIEvent(
      clickOnAnswer({
        source_name: mapPageTypeToSourceName(pageType),
        item_id: itemId,
        item_type: itemType,
        kb_lang: locale as string,
        clicked_url: url,
        clicked_item_id: id,
        clicked_item_order: `${index + 1}`,
        question: inputText,
        search_session_Id: searchSessionId,
        clicked_item_type: mapArticleTypeToItemType(type),
        search_result_selection:
          BI.SEARCH_RESULT_SELECTION.SEARCH_BAR_DROPDOWN_LIST,
      })
    );
    onClear();
  };

  useEffect(() => {
    onClear();
  }, [asPath]);

  const searchExternalResources: () => Promise<void> =
    isExternalSearchResultsEnabled ? externalSearch : () => Promise.resolve();

  useEffect(() => {
    Promise.all([search(), searchExternalResources()])
      .then()
      .catch(() => false);
  }, [inputText, search, externalSearch]);

  const handleKeyboardEvent = useCallback(
    async (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          setFocusedArticleIndex(
            focusedArticleIndex === articles?.length
              ? focusedArticleIndex
              : focusedArticleIndex + 1
          );
          break;
        case 'ArrowUp':
          setFocusedArticleIndex(
            focusedArticleIndex > -1
              ? focusedArticleIndex - 1
              : focusedArticleIndex
          );
          break;
        case 'Enter':
          if (
            articles &&
            focusedArticleIndex >= 0 &&
            focusedArticleIndex <= articles.length - 1
          ) {
            await onSearchResultClick(
              articles[focusedArticleIndex],
              focusedArticleIndex
            );
            await pushUrl(articles[focusedArticleIndex].uri);
          } else if (focusedArticleIndex === articles?.length) {
            await pushUrl(`/search?term=${inputText}`);
          }
          break;
      }
    },
    [articles, focusedArticleIndex, inputText]
  );

  const onSearchChange = useCallback((e: { target: { value: string } }) => {
    setInputText(e.target.value.trim());
    setFocusedArticleIndex(-1);
  }, []);

  const onManuallyInput = useCallback(
    async (val: string) => {
      if (focusedArticleIndex === -1) {
        setInputText(val.trim());
      }
    },
    [focusedArticleIndex]
  );

  const onSearchFocus = useCallback(() => {
    setHideResults(false);
    setFocusedArticleIndex(-1);
  }, []);

  const onClickAllResults = async () => {
    await sendBIEvent(
      clickTheSeeAllResultsLink({
        source_name: mapPageTypeToSourceName(pageType),
        item_id: itemId,
        item_type: itemType,
        kb_lang: locale as string,
        question: inputText,
        search_session_Id: searchSessionId,
      })
    );
    onClear();
  };

  const onSearchBarInputClicked = async () => {
    if (articles && articles.length > 0) {
      await pushUrl(`${locale}/search?term=${inputText}`);
    }
  };

  const areResultsPresent =
    (articles && articles.length > 0) ||
    (isExternalSearchResultsEnabled &&
      externalSearchResults &&
      externalSearchResults.length > 0);

  const isEmptyResultsVisible = !areResultsPresent && !isLoading;

  return (
    <div
      ref={ref}
      className={css.wrapper}
      data-hook={DATA_HOOKS.SEARCH_BAR}
      onKeyUp={handleKeyboardEvent}
    >
      <Box
        direction="vertical"
        verticalAlign="middle"
        className={`${
          pageType === PAGES.HOMEPAGE
            ? css.searchBarWrapperHomepage
            : css.searchBarWrapperSubHeader
        } ${homepageNewDesign ? css.homepageNewDesign : ''}`}
      >
        <Search
          status={
            isLoading && isExternalSearchResultsEnabled ? 'loading' : undefined
          }
          debounceMs={1000}
          maxLength={100}
          placeholder={t('searchbar.placeholder')}
          onChange={onSearchChange}
          onClear={onClear}
          onManuallyInput={onManuallyInput}
          value={inputText}
          className={css.searchBar}
          dataHook={DATA_HOOKS.SEARCH_BAR_INPUT}
          textOverflow={inputText.length > 0 ? undefined : 'ellipsis'}
          onEnterPressed={onSearchBarInputClicked}
          onFocus={onSearchFocus}
          autoFocus={pageType === PAGES.HOMEPAGE && homepageNewDesign}
        />
        {articles && inputText !== '' && !hideResults && (
          <Card
            showShadow
            className={classNames({
              [css.searchBarResultsWrapperHomepage]:
                pageType === PAGES.HOMEPAGE,
              [css.searchBarResultsWrapperSubHeader]:
                pageType !== PAGES.HOMEPAGE,
              [css.newSearchBarResults]: showNewSearchBarResults,
              [css.externalSearchEnabled]: isExternalSearchResultsEnabled,
            })}
          >
            {areResultsPresent &&
              (showNewSearchBarResults ? (
                <SearchBarResults
                  articles={articles}
                  externalSearchResults={externalSearchResults}
                  inputText={inputText}
                  focusedArticleIndex={focusedArticleIndex}
                  onClickAllResults={onClickAllResults}
                  onSearchResultClick={onSearchResultClick}
                  setFocusedArticleIndex={setFocusedArticleIndex}
                />
              ) : (
                <DeprecatedSearchBarResults
                  articles={articles}
                  inputText={inputText}
                  itemId={itemId}
                  itemType={itemType}
                  searchSessionId={searchSessionId}
                  pageType={pageType}
                  onClear={onClear}
                  focusedArticleIndex={focusedArticleIndex}
                  setFocusedArticleIndex={setFocusedArticleIndex}
                  onSearchResultClick={onSearchResultClick}
                />
              ))}
            {isEmptyResultsVisible &&
              (showNewSearchBarResults ? (
                <EmptySearchResults inputText={inputText} />
              ) : (
                <DeprecatedEmptySearchResults />
              ))}
          </Card>
        )}
      </Box>
    </div>
  );
};
